/**
 * Os componentes de **todos** os projetos — o "Componentes Exportar" do menu antigo.
 *
 * **Não é a busca de componente do projeto.** Aquela (`api/componentes`) exige um
 * projeto; esta existe para atravessar os 921 de uma vez. São 64.631 componentes.
 *
 * **A lista é para conferir, o arquivo é para levar.** A tela antiga joga as 64
 * mil linhas dentro de uma tabela HTML e deixa o navegador montar o CSV. Aqui a
 * listagem é paginada de 100 e o arquivo sai do banco direto para a resposta, em
 * streaming: o recorte inteiro saiu em 2,5 s e 6,1 MB.
 */

import { useMutation, useQuery } from "@tanstack/react-query"
import { api, foraDoProjeto, obter } from "./cliente"

export type ComponenteDeQualquerProjeto = {
  componenteId: number
  projetoId: number
  projetoDescricao: string
  descricao: string
  apelido: string
  codigo: string
}

export type PaginaDeComponentes = {
  itens: ComponenteDeQualquerProjeto[]
  /** Quantos atendem ao filtro — não quantos vieram nesta página. */
  total: number
  pagina: number
  porPagina: number
}

export type FiltroDeComponentes = { projetoId: number; termo: string }

export function usarComponentesDeTodosOsProjetos(filtro: FiltroDeComponentes, pagina: number) {
  return useQuery({
    queryKey: ["componentes-todos", filtro.projetoId, filtro.termo, pagina] as const,
    staleTime: 30_000,
    queryFn: () => obter<PaginaDeComponentes>("api/componentes/todos", {
      projetoId: filtro.projetoId > 0 ? filtro.projetoId : undefined,
      termo: filtro.termo.trim() === "" ? undefined : filtro.termo.trim(),
      pagina,
    }),
  })
}

/**
 * Baixa o recorte inteiro em CSV.
 *
 * Passa pelo axios, e não por um `<a href>`, porque o token vive em memória e
 * não em cookie — um link direto sairia sem autenticação.
 */
export function usarExportarComponentes() {
  return useMutation({
    mutationFn: async (filtro: FiltroDeComponentes) => {
      const params: Record<string, string | number> = {}
      if (filtro.projetoId > 0) params.projetoId = filtro.projetoId
      if (filtro.termo.trim() !== "") params.termo = filtro.termo.trim()

      const r = await api.get("api/componentes/todos/exportar", {
        ...foraDoProjeto({ params }),
        responseType: "blob",
        // A resposta é texto, não JSON — o interceptor que recusa HTML não se aplica.
        transformResponse: (d) => d,
        timeout: 300_000,
      })

      const nome = nomeNoCabecalho(r.headers?.["content-disposition"])
      baixar(r.data as Blob, nome)
      return nome
    },
  })
}

function nomeNoCabecalho(cabecalho: unknown): string {
  const texto = typeof cabecalho === "string" ? cabecalho : ""
  const achado = /filename="?([^";]+)"?/i.exec(texto)
  return achado?.[1] ?? "componentes.csv"
}

function baixar(conteudo: Blob, nome: string) {
  const url = URL.createObjectURL(conteudo)
  const a = document.createElement("a")
  a.href = url
  a.download = nome
  document.body.appendChild(a)
  a.click()
  a.remove()
  // Sem isso o Blob fica preso na memória da aba até o recarregamento.
  URL.revokeObjectURL(url)
}
